import React from "react";
import ReactApexChart from "react-apexcharts";
import { useQuery } from "react-query";
import {
  Users,
  BookOpen,
  ShieldCheck,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  UserCheck,
  FlaskConical,
  MessageSquare,
  ClipboardCheck,
} from "lucide-react";
import { getDashboardStats, getRegistrationStat, getTransactions } from "services/dashboard.service";
import { getAllVerificationOrders } from "services/verification.service";
import { DashboardStat, ITransaction } from "types/dashboard.service";

function KpiCard({
  label,
  value,
  icon: Icon,
  color,
  sub,
  trend,
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  sub?: string;
  trend?: { value: number; up: boolean };
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-start gap-4 shadow-sm">
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon size={20} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5 truncate">{sub}</p>}
        {trend && (
          <p className={`text-xs mt-1 flex items-center gap-0.5 ${trend.up ? "text-green-600" : "text-red-500"}`}>
            {trend.up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {trend.value}% vs last month
          </p>
        )}
      </div>
    </div>
  );
}

function RegistrationChart() {
  const end = new Date().toISOString().split("T")[0];
  const start = new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0];
  const { data } = useQuery(["reg-chart", start, end], () =>
    getRegistrationStat(start, end)
  );

  const series = [{ name: "Registrations", data: data?.data?.plots ?? [] }];
  const options: ApexCharts.ApexOptions = {
    chart: { type: "area", toolbar: { show: false }, sparkline: { enabled: false } },
    stroke: { curve: "smooth", width: 2 },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05 } },
    colors: ["#6366f1"],
    xaxis: { categories: data?.data?.labels ?? [], labels: { style: { fontSize: "11px" } } },
    yaxis: { labels: { style: { fontSize: "11px" } } },
    grid: { borderColor: "#f1f5f9" },
    dataLabels: { enabled: false },
    tooltip: { theme: "light" },
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-800 mb-4">
        User Registrations — Last 30 Days
      </h3>
      <ReactApexChart options={options} series={series} type="area" height={220} />
    </div>
  );
}

function GenderChart({ gender }: { gender: DashboardStat["gender"] }) {
  if (!gender?.length) return null;
  const series = gender.map((g) => g.count);
  const labels = gender.map((g) => g.gender ?? "Unknown");
  const options: ApexCharts.ApexOptions = {
    chart: { type: "donut" },
    labels,
    colors: ["#6366f1", "#f472b6", "#94a3b8"],
    legend: { position: "bottom", fontSize: "12px" },
    dataLabels: { enabled: true },
    plotOptions: { pie: { donut: { size: "65%" } } },
  };
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-800 mb-4">Gender Distribution</h3>
      <ReactApexChart options={options} series={series} type="donut" height={220} />
    </div>
  );
}

function CategoryChart({ category }: { category: DashboardStat["category"] }) {
  if (!category?.length) return null;
  const top = [...category].sort((a, b) => b.count - a.count).slice(0, 8);
  const series = [{ name: "Workers", data: top.map((c) => c.count) }];
  const options: ApexCharts.ApexOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    colors: ["#6366f1"],
    xaxis: { categories: top.map((c) => c.occupation ?? "—"), labels: { style: { fontSize: "11px" } } },
    yaxis: { labels: { style: { fontSize: "11px" } } },
    grid: { borderColor: "#f1f5f9" },
    dataLabels: { enabled: false },
  };
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-800 mb-4">Top Occupations</h3>
      <ReactApexChart options={options} series={series} type="bar" height={220} />
    </div>
  );
}

function LocationChart({ location }: { location: DashboardStat["location"] }) {
  if (!location?.length) return null;
  const top = [...location].sort((a, b) => b.count - a.count).slice(0, 8);
  const series = [{ name: "Workers", data: top.map((l) => l.count) }];
  const options: ApexCharts.ApexOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { borderRadius: 4 } },
    colors: ["#22d3ee"],
    xaxis: { categories: top.map((l) => l.state ?? "—"), labels: { style: { fontSize: "11px" }, rotate: -35 } },
    grid: { borderColor: "#f1f5f9" },
    dataLabels: { enabled: false },
  };
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-800 mb-4">Workers by State</h3>
      <ReactApexChart options={options} series={series} type="bar" height={220} />
    </div>
  );
}

function RecentTransactions() {
  const { data } = useQuery(["recent-tx"], () =>
    getTransactions({ page: 1, limit: 8 })
  );
  const txs: ITransaction[] = (data?.data as any)?.data ?? [];

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-800 mb-4">Recent Transactions</h3>
      {txs.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">No transactions yet</p>
      ) : (
        <div className="divide-y divide-gray-50">
          {txs.map((tx) => (
            <div key={tx._id} className="flex items-center gap-3 py-2.5">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${tx.type === "Credit" ? "bg-green-500" : "bg-red-400"}`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-800 truncate">
                  {tx.narration ?? tx.type}
                </p>
                <p className="text-xs text-gray-400">
                  {tx.user?.firstName} {tx.user?.lastName}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className={`text-xs font-semibold ${tx.type === "Credit" ? "text-green-600" : "text-red-500"}`}>
                  {tx.type === "Credit" ? "+" : "-"}₦{Number(tx.amount).toLocaleString()}
                </p>
                <p className="text-xs text-gray-400">
                  {new Date(tx.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VerificationOrdersSummary() {
  const { data } = useQuery(["verif-summary"], () => getAllVerificationOrders());
  const orders: any[] = data?.data ?? [];
  const pending = orders.filter((o) => ["paid", "in_progress"].includes(o.status)).length;
  const completed = orders.filter((o) => o.status === "completed").length;
  const passed = orders.filter((o) => o.status === "completed" && o.result?.passed).length;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-800 mb-4">Verification Orders</h3>
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Pending review</span>
          <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">{pending}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Completed</span>
          <span className="text-xs font-bold text-gray-700">{completed}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Passed</span>
          <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">{passed}</span>
        </div>
        <div className="pt-2 border-t border-gray-50">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-400">Pass rate</span>
            <span className="text-gray-600 font-medium">
              {completed > 0 ? Math.round((passed / completed) * 100) : 0}%
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5">
            <div
              className="bg-green-500 h-1.5 rounded-full"
              style={{ width: `${completed > 0 ? Math.round((passed / completed) * 100) : 0}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data, isLoading } = useQuery(["dashboard-stats"], getDashboardStats);
  const stats: DashboardStat | undefined = data?.data;

  const totalWorkers = (stats?.verified ?? 0) + (stats?.unverified ?? 0);
  const activeWorkers = stats?.active ?? 0;

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          label="Total Workers"
          value={isLoading ? "—" : totalWorkers.toLocaleString()}
          icon={Users}
          color="bg-indigo-500"
          sub={`${activeWorkers} currently active`}
        />
        <KpiCard
          label="Verified Workers"
          value={isLoading ? "—" : (stats?.verified ?? 0).toLocaleString()}
          icon={ShieldCheck}
          color="bg-emerald-500"
          sub="Holds at least one badge"
        />
        <KpiCard
          label="Active Listings"
          value={isLoading ? "—" : activeWorkers.toLocaleString()}
          icon={BookOpen}
          color="bg-sky-500"
        />
        <KpiCard
          label="Completion Rate"
          value={isLoading ? "—" : `${stats?.completionRate?.length ?? 0} profiles`}
          icon={TrendingUp}
          color="bg-violet-500"
          sub="Submitted at least one KYC section"
        />
      </div>

      {/* Operations KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          label="Total Placements"
          value={isLoading ? "—" : (stats?.totalPlacements ?? 0).toLocaleString()}
          icon={UserCheck}
          color="bg-teal-500"
          sub={`${(stats?.activePlacements ?? 0).toLocaleString()} currently active`}
        />
        <KpiCard
          label="Lab Test Orders"
          value={isLoading ? "—" : (stats?.totalLabOrders ?? 0).toLocaleString()}
          icon={FlaskConical}
          color="bg-amber-500"
        />
        <KpiCard
          label="Conversations"
          value={isLoading ? "—" : (stats?.totalConversations ?? 0).toLocaleString()}
          icon={MessageSquare}
          color="bg-rose-500"
        />
        <KpiCard
          label="Inspections"
          value={isLoading ? "—" : (stats?.totalInspections ?? 0).toLocaleString()}
          icon={ClipboardCheck}
          color="bg-cyan-500"
        />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <RegistrationChart />
        </div>
        <VerificationOrdersSummary />
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <GenderChart gender={stats?.gender ?? []} />
        <CategoryChart category={stats?.category ?? []} />
        <LocationChart location={stats?.location ?? []} />
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <RecentTransactions />
        {/* Profile completion leaderboard */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Profile Completion — Top Workers
          </h3>
          {(stats?.completionRate ?? []).slice(0, 8).map((u) => (
            <div key={u.userId} className="flex items-center gap-3 py-2">
              <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700 flex-shrink-0">
                {u.firstName?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-800 truncate">
                  {u.firstName} {u.lastName}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                    <div
                      className="bg-indigo-500 h-1.5 rounded-full"
                      style={{ width: `${Math.round(u.submissionRate * 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {Math.round(u.submissionRate * 100)}%
                  </span>
                </div>
              </div>
            </div>
          ))}
          {!stats?.completionRate?.length && (
            <p className="text-sm text-gray-400 text-center py-6">No data yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
